import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { computeOrderCommercialTotals } from "@/lib/commercialEngine";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD, FORM } from "@/constants/testIds";
import { formatDateTime } from "@/lib/dateUtils";
import DynamicFieldInputs from "@/pages/financial/DynamicFieldInputs";
import AddProductRouter from "@/components/catalog_engine/AddProductRouter";
import CatalogPicker from "@/components/CatalogPicker";
import ServiceItemForm from "@/components/ServiceItemForm";

const STATUS_OPTIONS = [
  "design",
  "waiting_approval",
  "production",
  "ready",
  "delivered",
];

const blankItem = () => ({
  product_code: "",
  description: "",
  quantity: 1,
  notes: "",
  // Sprint 4 — catalog linkage (all optional)
  product_id: null,
  product_type_id: null,
  custom_fields: {},
  unit_price: 0,
  unit_cost: 0,
  // Sprint 5.3 — structured production fields (all optional)
  decoration_method_id: null,
  logo_location_id: null,
  // Iter44 · Fase B — hardcoded position name (used when the decoration
  // method is one of DTF / Embroidery / Leather Patch). Coexists with
  // logo_location_id for retro-compat with DB-backed positions.
  logo_location_custom: "",
  colors_used: "",
  artwork_url: "",
  customer_notes: "",
  width: null,
  height: null,
  thickness: null,
  area_sqft: null,
  // V2 master-catalog flag mirrored from linked product (drives W×H prompts)
  requires_dimensions: false,
  // Sprint Smart Products V3 → Orders
  smart_product_id: null,
  smart_product_snapshot: null, // {name, code, image_url, required_fields, default_brand_id, default_model, default_supplier_id, uom_id, production_time_*}
  brand_id: null,
  model: "",
  supplier_id: null,
  uom_id: null,
  color_id: null,
  size_id: null,
  material_id: null,
  installation: false,
  led: false,
  production_time_value: null,
  production_time_unit: null,
  image_url: null,
  // Phase-1 Add Product architecture (catalog + service)
  source_type: null,
  catalog_product_id: null,
  catalog_sku: "",
  catalog_color: "",
  catalog_size: "",
  catalog_image_url: "",
  service_type: "",
  // Iter42 · Fase B — per-product extra costs (only editable/visible to
  // users with costs.view; legacy items load with empty list).
  extra_costs: [],
  // Iter46 · Fase C — Motor Comercial snapshot (all optional, retro-compat).
  commercial_group: null,
  commercial_tier: null,
  commercial_uplifts: [],
  manual_price_override: false,
});

// Derive initial items array: prefer existing items, else fallback to legacy product string
function initialItems(initial) {
  if (initial?.items && Array.isArray(initial.items) && initial.items.length > 0) {
    return initial.items.map((it) => ({
      product_code: it.product_code || "",
      description: it.description || "",
      quantity: it.quantity ?? 1,
      notes: it.notes || "",
      product_id: it.product_id || null,
      product_type_id: it.product_type_id || null,
      custom_fields: it.custom_fields || {},
      unit_price: it.unit_price ?? 0,
      unit_cost: it.unit_cost ?? 0,
      // Sprint 5.3 fields (legacy items load as empty/null)
      decoration_method_id: it.decoration_method_id || null,
      logo_location_id: it.logo_location_id || null,
      logo_location_custom: it.logo_location_custom || "",
      colors_used: it.colors_used || "",
      artwork_url: it.artwork_url || "",
      customer_notes: it.customer_notes || "",
      width: it.width ?? null,
      height: it.height ?? null,
      thickness: it.thickness ?? null,
      area_sqft: it.area_sqft ?? null,
      requires_dimensions: !!it.requires_dimensions,
      // Smart Products V3 hydrate (legacy items load as null/empty)
      smart_product_id: it.smart_product_id || null,
      smart_product_snapshot: null, // re-hydrated from smartProducts list when rendered
      brand_id: it.brand_id || null,
      model: it.model || "",
      supplier_id: it.supplier_id || null,
      uom_id: it.uom_id || null,
      color_id: it.color_id || null,
      size_id: it.size_id || null,
      material_id: it.material_id || null,
      installation: !!it.installation,
      led: !!it.led,
      production_time_value: it.production_time_value ?? null,
      production_time_unit: it.production_time_unit || null,
      image_url: it.image_url || null,
      // Phase-1 Add Product architecture (catalog + service traceability)
      source_type: it.source_type || null,
      catalog_product_id: it.catalog_product_id || null,
      catalog_sku: it.catalog_sku || "",
      catalog_color: it.catalog_color || "",
      catalog_size: it.catalog_size || "",
      catalog_image_url: it.catalog_image_url || "",
      service_type: it.service_type || "",
      // Iter42 · Fase B — hydrate per-product extras from backend response
      // (backend returns [] for legacy items and scrubs to null for users
      // without costs.view — we coerce back to [] to keep the editor stable).
      extra_costs: Array.isArray(it.extra_costs) ? it.extra_costs : [],
      // Iter46 · Fase C — commercial snapshot hydration.
      commercial_group: it.commercial_group || null,
      commercial_tier: it.commercial_tier || null,
      commercial_uplifts: Array.isArray(it.commercial_uplifts)
        ? it.commercial_uplifts
        : [],
      manual_price_override: !!it.manual_price_override,
    }));
  }
  if (initial?.product) {
    return [{ ...blankItem(), description: initial.product }];
  }
  return [blankItem()];
}

const money = (n) => {
  const num = Number(n || 0);
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function OrderForm({ initial, onSubmit, onCancel, onPdfChanged }) {
  const { t, lang } = useI18n();
  const { can } = useAuth();
  // Iter42 · Fase B — gate every financial UI element (unit_cost, extras,
  // cost_total, profit, margin) behind costs.view. Regular employees do
  // not see cost data anywhere (backend also strips it from responses).
  const hasFinancialAccess = can("costs", "view");
  const [invoiceNumber, setInvoiceNumber] = useState(initial?.invoice_number || "");
  const [companyName, setCompanyName] = useState(initial?.company_name || "");
  const [contactPerson, setContactPerson] = useState(initial?.contact_person || "");
  const [customerPhone, setCustomerPhone] = useState(initial?.customer_phone || "");
  const [items, setItems] = useState(() => initialItems(initial));
  const [delivery, setDelivery] = useState(initial?.delivery_date?.slice(0, 10) || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [status, setStatus] = useState(initial?.status || "design");
  const [totalAmount, setTotalAmount] = useState(
    initial?.total_amount != null ? String(initial.total_amount) : "0"
  );
  const [depositReceived, setDepositReceived] = useState(
    initial?.deposit_received != null ? String(initial.deposit_received) : "0"
  );
  // Iter46 · Fase C — Motor Comercial · order-level discount + Sales Tax.
  const [discountType, setDiscountType] = useState(
    initial?.discount_type || "amount"
  );
  const [discountValue, setDiscountValue] = useState(
    initial?.discount_value != null ? String(initial.discount_value) : "0"
  );
  const [taxExempt, setTaxExempt] = useState(!!initial?.tax_exempt);
  const [salesTaxRate] = useState(
    initial?.sales_tax_rate != null ? Number(initial.sales_tax_rate) : 0.0975
  );
  const [loading, setLoading] = useState(false);
  // Sprint 4 — catalog data for product picker + dynamic fields
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  // Sprint 5.3 — V2 catalogs for structured production fields
  const [decorationMethods, setDecorationMethods] = useState([]);
  const [logoLocations, setLogoLocations] = useState([]);
  // Sprint Smart Products V3 → Orders integration
  const [smartProducts, setSmartProducts] = useState([]);
  const [colorsCatalog, setColorsCatalog] = useState([]);
  const [sizesCatalog, setSizesCatalog] = useState([]);
  const [materialsCatalog, setMaterialsCatalog] = useState([]);
  const [brandsCatalog, setBrandsCatalog] = useState([]);
  const [suppliersCatalog, setSuppliersCatalog] = useState([]);
  const [uomsCatalog, setUomsCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, tRes, dRes, lRes, spRes, coRes, siRes, maRes, brRes, suRes, uoRes] =
          await Promise.all([
            axios.get(`${API}/financial-products`, { withCredentials: true }),
            axios.get(`${API}/product-types`, { withCredentials: true }),
            axios.get(`${API}/decoration-methods`, { withCredentials: true }),
            axios.get(`${API}/logo-locations`, { withCredentials: true }),
            axios.get(`${API}/smart-products?include_inactive=false`, { withCredentials: true }),
            axios.get(`${API}/colors`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/sizes`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/materials`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/brands`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/units-of-measure`, { withCredentials: true }).catch(() => ({ data: [] })),
          ]);
        if (cancelled) return;
        setCatalogProducts(Array.isArray(pRes.data) ? pRes.data : []);
        setProductTypes(Array.isArray(tRes.data) ? tRes.data : []);
        setDecorationMethods(Array.isArray(dRes.data) ? dRes.data : []);
        setLogoLocations(Array.isArray(lRes.data) ? lRes.data : []);
        setSmartProducts(Array.isArray(spRes.data) ? spRes.data : []);
        setColorsCatalog(Array.isArray(coRes.data) ? coRes.data : []);
        setSizesCatalog(Array.isArray(siRes.data) ? siRes.data : []);
        setMaterialsCatalog(Array.isArray(maRes.data) ? maRes.data : []);
        setBrandsCatalog(Array.isArray(brRes.data) ? brRes.data : []);
        setSuppliersCatalog(Array.isArray(suRes.data) ? suRes.data : []);
        setUomsCatalog(Array.isArray(uoRes.data) ? uoRes.data : []);
      } catch {
        // Silent: catalog is optional, manual items still work
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Invoice PDF attachment (edit mode only — order must already exist)
  const orderId = initial?.id || null;
  const fileInputRef = useRef(null);
  const [invoicePdfFilename, setInvoicePdfFilename] = useState(
    initial?.invoice_pdf_filename || null
  );
  const [invoicePdfUploadedAt, setInvoicePdfUploadedAt] = useState(
    initial?.invoice_pdf_uploaded_at || null
  );
  const [pdfUploading, setPdfUploading] = useState(false);

  useEffect(() => {
    setInvoiceNumber(initial?.invoice_number || "");
    setCompanyName(initial?.company_name || "");
    setContactPerson(initial?.contact_person || "");
    setCustomerPhone(initial?.customer_phone || "");
    setItems(initialItems(initial));
    setDelivery(initial?.delivery_date?.slice(0, 10) || "");
    setNotes(initial?.notes || "");
    setStatus(initial?.status || "design");
    setTotalAmount(initial?.total_amount != null ? String(initial.total_amount) : "0");
    setDepositReceived(
      initial?.deposit_received != null ? String(initial.deposit_received) : "0"
    );
    setInvoicePdfFilename(initial?.invoice_pdf_filename || null);
    setInvoicePdfUploadedAt(initial?.invoice_pdf_uploaded_at || null);
  }, [initial]);

  const total = useMemo(() => parseFloat(totalAmount) || 0, [totalAmount]);
  const deposit = useMemo(() => parseFloat(depositReceived) || 0, [depositReceived]);
  const balance = useMemo(() => Math.max(total - deposit, -9999999), [total, deposit]);

  // ----- Item financial computation (Sprint 4) -----
  const productById = useMemo(() => {
    const m = {};
    catalogProducts.forEach((p) => { m[p.id] = p; });
    return m;
  }, [catalogProducts]);
  const typeById = useMemo(() => {
    const m = {};
    productTypes.forEach((pt) => { m[pt.id] = pt; });
    return m;
  }, [productTypes]);
  // Smart Products map for O(1) lookup during render + autofill.
  const smartProductById = useMemo(() => {
    const m = {};
    smartProducts.forEach((p) => { m[p.id] = p; });
    return m;
  }, [smartProducts]);

  // Recompute order-level rollup live (display-only — backend is source of truth)
  const liveItems = useMemo(() => {
    return items.map((it) => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unit_price) || 0;
      const cost = Number(it.unit_cost) || 0;
      // Iter42 · Fase B — aggregate extra_costs per item.
      // per_unit → amount × qty, flat → amount.
      const extras = Array.isArray(it.extra_costs) ? it.extra_costs : [];
      const extras_total = extras.reduce((s, ec) => {
        const amt = Number(ec?.amount) || 0;
        return s + (ec?.type === "flat" ? amt : amt * qty);
      }, 0);
      const base_cost_total = cost * qty;
      const cost_total = base_cost_total + extras_total;
      const subtotal = price * qty;
      return {
        ...it,
        subtotal: +subtotal.toFixed(2),
        base_cost_total: +base_cost_total.toFixed(2),
        extra_costs_total: +extras_total.toFixed(2),
        cost_total: +cost_total.toFixed(2),
        profit: +(subtotal - cost_total).toFixed(2),
      };
    });
  }, [items]);

  const orderSubtotal = useMemo(
    () => liveItems.reduce((s, it) => s + (it.subtotal || 0), 0),
    [liveItems]
  );
  const orderCost = useMemo(
    () => liveItems.reduce((s, it) => s + (it.cost_total || 0), 0),
    [liveItems]
  );
  const orderProfit = +(orderSubtotal - orderCost).toFixed(2);
  const orderMargin = orderSubtotal > 0 ? +((orderProfit / orderSubtotal) * 100).toFixed(2) : 0;
  // When catalog pricing is in use, total_amount mirrors subtotal automatically.
  const anyPriced = liveItems.some((it) => Number(it.unit_price) > 0);

  // Iter46 · Fase C — Motor Comercial: order-level discount + Sales Tax
  // live rollup. Kicks in only when catalog pricing is active so legacy
  // manual orders keep behaving identically.
  const commercialRollup = useMemo(() => {
    if (!anyPriced) {
      return {
        discount_amount: 0,
        taxable_amount: orderSubtotal,
        sales_tax_amount: 0,
        total_amount: parseFloat(totalAmount) || 0,
        balance_due: Math.max((parseFloat(totalAmount) || 0) - (parseFloat(depositReceived) || 0), 0),
      };
    }
    return computeOrderCommercialTotals({
      subtotal: orderSubtotal,
      discountType,
      discountValue: parseFloat(discountValue) || 0,
      taxExempt,
      salesTaxRate,
      depositReceived: parseFloat(depositReceived) || 0,
    });
  }, [
    anyPriced,
    orderSubtotal,
    discountType,
    discountValue,
    taxExempt,
    salesTaxRate,
    depositReceived,
    totalAmount,
  ]);

  // Sync total field UI when catalog drives it (post-discount + tax now).
  useEffect(() => {
    if (anyPriced) {
      const next = commercialRollup.total_amount.toFixed(2);
      if (next !== totalAmount) setTotalAmount(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercialRollup.total_amount, anyPriced]);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  // Bind an item to a Smart Product → snapshot SP defaults locally so the
  // user sees the inherited values immediately. NEVER overwrites a value the
  // user already typed (per-item overrides win).
  const linkItemToSmartProduct = (idx, smartProductId) => {
    if (!smartProductId) {
      updateItem(idx, {
        smart_product_id: null,
        smart_product_snapshot: null,
        // Keep entered values; legacy fallback fields stay as-is.
      });
      return;
    }
    const sp = smartProductById[smartProductId];
    if (!sp) return;
    updateItem(idx, {
      smart_product_id: sp.id,
      smart_product_snapshot: sp,
      product_code: sp.code || "",
      description: sp.name || "",
      // Inherit defaults but only if the row was blank.
      brand_id: sp.default_brand_id || null,
      model: sp.default_model || "",
      supplier_id: sp.default_supplier_id || null,
      uom_id: sp.uom_id || null,
      production_time_value: sp.production_time_value ?? null,
      production_time_unit: sp.production_time_unit || null,
      image_url: sp.image_url || null,
      notes: sp.description || "",
    });
  };

  // Bind an item to a catalog product → snapshot its fields locally
  const linkItemToProduct = (idx, productId) => {
    if (!productId) {
      // Unlink → keep current row but clear linkage; keep description/qty/price as-is.
      updateItem(idx, {
        product_id: null,
        product_type_id: null,
        custom_fields: {},
        requires_dimensions: false,
      });
      return;
    }
    const p = productById[productId];
    if (!p) return;
    updateItem(idx, {
      product_id: p.id,
      product_code: p.sku || "",
      description: p.name || "",
      product_type_id: p.product_type_id || null,
      custom_fields: {},
      unit_price: Number(p.suggested_price || 0),
      unit_cost: Number(p.average_cost || 0),
      // Sprint 5.3 — mirror V2 master-catalog flag onto the item
      requires_dimensions: !!p.requires_dimensions,
    });
  };

  const updateItemCustomField = (idx, key, value) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        return { ...it, custom_fields: { ...(it.custom_fields || {}), [key]: value } };
      })
    );
  };
  const addItem = () => setItems((prev) => [...prev, blankItem()]);
  // Phase-1 Add Product architecture — router modal + two additive flows.
  // Feb-2026 v2: replaced flat 3-option picker with the scalable
  // AddProductRouter (Rawen's Inventory · Purchasing Center → suppliers).
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);

  // Handler called by the AddProductRouter.
  const handlePickAddOption = (arg) => {
    // Backwards-compat: the legacy AddItemPicker passed a raw string.
    const kind = typeof arg === "string" ? arg : arg?.kind;
    setShowAddPicker(false);
    if (kind === "internal_custom" || kind === "custom") {
      addItem();
    } else if (kind === "internal_catalog" || kind === "catalog") {
      setShowCatalog(true);
    } else if (kind === "internal_service" || kind === "service") {
      setShowServiceForm(true);
    }
  };

  // Both catalog and service flows return an array of item payloads.
  const appendCatalogOrServiceItems = (newItems) => {
    if (!Array.isArray(newItems) || newItems.length === 0) return;
    setItems((prev) => {
      // If the very first row is a blank stub (no data at all), replace it.
      // Otherwise append. This mirrors what a user expects when they open
      // an empty order form and add their first catalog item.
      const first = prev[0];
      const isFirstBlank =
        prev.length === 1 &&
        first &&
        !first.product_code &&
        !first.description &&
        !first.product_id &&
        !first.smart_product_id &&
        !first.source_type &&
        (first.quantity === 1 || first.quantity == null) &&
        !first.notes;
      const enriched = newItems.map((it) => ({ ...blankItem(), ...it }));
      return isFirstBlank ? enriched : [...prev, ...enriched];
    });
  };
  // GP-001 fix: allow removing any item — including the last one. If the
  // user removes the last item we replace it with a fresh blank row so the
  // form stays usable (financial rollup will pick this up automatically via
  // the existing `liveItems` useMemo → setItems triggers re-render → totals
  // recalculate on the next commit).
  const removeItem = (idx) =>
    setItems((prev) => {
      if (prev.length <= 1) return [blankItem()];
      return prev.filter((_, i) => i !== idx);
    });

  // ----- Invoice PDF handlers -----
  const handleInvoicePdfClick = () => {
    if (!orderId) {
      toast.info(t("invoice_pdf_save_first"));
      return;
    }
    fileInputRef.current && fileInputRef.current.click();
  };

  const handleInvoicePdfChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ""; // allow re-selecting same file later
    if (!file) return;
    if (!orderId) {
      toast.info(t("invoice_pdf_save_first"));
      return;
    }
    const isPdf =
      (file.type || "").toLowerCase() === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error(t("invoice_pdf_invalid"));
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setPdfUploading(true);
    try {
      const { data } = await axios.post(
        `${API}/orders/${orderId}/invoice-pdf`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setInvoicePdfFilename(data.invoice_pdf_filename || file.name);
      setInvoicePdfUploadedAt(data.invoice_pdf_uploaded_at || new Date().toISOString());
      toast.success(t("invoice_pdf_uploaded"));
      onPdfChanged && onPdfChanged({
        orderId,
        invoice_pdf_filename: data.invoice_pdf_filename || file.name,
        invoice_pdf_size: data.invoice_pdf_size,
        invoice_pdf_uploaded_at: data.invoice_pdf_uploaded_at || new Date().toISOString(),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setPdfUploading(false);
    }
  };

  const handleInvoicePdfView = () => {
    if (!orderId) return;
    window.open(`${API}/orders/${orderId}/invoice-pdf`, "_blank", "noopener");
  };

  const handleInvoicePdfRemove = async () => {
    if (!orderId) return;
    if (!window.confirm(t("confirm_delete"))) return;
    setPdfUploading(true);
    try {
      await axios.delete(`${API}/orders/${orderId}/invoice-pdf`);
      setInvoicePdfFilename(null);
      setInvoicePdfUploadedAt(null);
      toast.success(t("invoice_pdf_deleted"));
      onPdfChanged && onPdfChanged({
        orderId,
        invoice_pdf_filename: null,
        invoice_pdf_size: null,
        invoice_pdf_uploaded_at: null,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setPdfUploading(false);
    }
  };

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Clean items: drop fully-empty rows; coerce numeric fields
      const cleanItems = items
        .map((it) => {
          // Sprint 5.3 — coerce dimensions and auto-derive area_sqft when blank
          const w = it.width === "" || it.width == null ? null : Number(it.width);
          const h = it.height === "" || it.height == null ? null : Number(it.height);
          const th = it.thickness === "" || it.thickness == null ? null : Number(it.thickness);
          let area = it.area_sqft === "" || it.area_sqft == null ? null : Number(it.area_sqft);
          if (it.requires_dimensions && (area == null || area === 0) && w != null && h != null) {
            area = +(w * h).toFixed(2);
          }
          return {
            product_code: (it.product_code || "").trim(),
            description: (it.description || "").trim(),
            quantity: Number(it.quantity) || 1,
            notes: (it.notes || "").trim(),
            // Sprint 4 — financial fields (all optional, backend snapshots + recomputes)
            product_id: it.product_id || null,
            product_type_id: it.product_type_id || null,
            custom_fields: it.custom_fields || {},
            unit_price: Number(it.unit_price) || 0,
            unit_cost: Number(it.unit_cost) || 0,
            // Sprint 5.3 — structured production fields (all optional)
            decoration_method_id: it.decoration_method_id || null,
            logo_location_id: it.logo_location_id || null,
            logo_location_custom: (it.logo_location_custom || "").trim(),
            colors_used: (it.colors_used || "").trim(),
            artwork_url: (it.artwork_url || "").trim(),
            customer_notes: (it.customer_notes || "").trim(),
            width: w,
            height: h,
            thickness: th,
            area_sqft: area,
            // Sprint Smart Products V3 → Orders
            smart_product_id: it.smart_product_id || null,
            brand_id: it.brand_id || null,
            model: (it.model || "").trim() || "",
            supplier_id: it.supplier_id || null,
            uom_id: it.uom_id || null,
            color_id: it.color_id || null,
            size_id: it.size_id || null,
            material_id: it.material_id || null,
            installation: !!it.installation,
            led: !!it.led,
            production_time_value:
              it.production_time_value === "" || it.production_time_value == null
                ? null
                : Number(it.production_time_value),
            production_time_unit: it.production_time_unit || null,
            image_url: it.image_url || null,
            // Phase-1 Add Product architecture (catalog + service traceability)
            source_type: it.source_type || null,
            catalog_product_id: it.catalog_product_id || null,
            catalog_sku: it.catalog_sku || "",
            catalog_color: it.catalog_color || "",
            catalog_size: it.catalog_size || "",
            catalog_image_url: it.catalog_image_url || "",
            service_type: it.service_type || "",
            // Iter42 · Fase B — persist per-product extras. Filter blanks
            // and normalize `type` to backend enum ('per_unit'|'flat').
            extra_costs: (Array.isArray(it.extra_costs) ? it.extra_costs : [])
              .filter((ec) => (ec?.concept || "").trim() || Number(ec?.amount) > 0)
              .map((ec) => ({
                id: ec.id || undefined,
                concept: (ec.concept || "").trim(),
                amount: Number(ec.amount) || 0,
                type: ec.type === "flat" ? "flat" : "per_unit",
              })),
            // Iter46 · Fase C — commercial engine snapshot (all optional).
            commercial_group: it.commercial_group || null,
            commercial_tier: it.commercial_tier || null,
            commercial_uplifts: Array.isArray(it.commercial_uplifts)
              ? it.commercial_uplifts
              : [],
            manual_price_override: !!it.manual_price_override,
          };
        })
        .filter(
          (it) =>
            it.product_code ||
            it.description ||
            it.notes ||
            it.product_id ||
            it.smart_product_id ||
            it.source_type ||
            it.unit_price > 0 ||
            (it.quantity && it.quantity !== 1)
        );

      // Backend will map items[0].description to legacy 'product' field automatically.
      const legacyProduct =
        cleanItems.length > 0 ? cleanItems[0].description : (initial?.product || "");

      await onSubmit({
        invoice_number: invoiceNumber.trim(),
        company_name: companyName.trim(),
        contact_person: contactPerson.trim(),
        customer_phone: customerPhone.trim(),
        product: legacyProduct,
        items: cleanItems,
        delivery_date: delivery,
        notes,
        status,
        total_amount: total,
        deposit_received: deposit,
        // Iter46 · Fase C — order-level commercial payload. Backend re-runs
        // the engine on save so these are inputs, not authoritative.
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
        tax_exempt: !!taxExempt,
        sales_tax_rate: salesTaxRate,
      });
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full border border-[var(--border)] bg-white px-3 py-2 focus:border-[var(--primary)]";

  return (
    <form onSubmit={handle} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("invoice_number")}</label>
          <input
            data-testid="form-order-invoice"
            required
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className={input}
          />
        </div>
        <div>
          <label className="label-overline block mb-1">{t("company_name")}</label>
          <input
            data-testid={FORM.orderCustomer}
            required
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className={input}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("contact_person")}</label>
          <input
            data-testid="form-order-contact"
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            className={input}
            placeholder="—"
          />
        </div>
        <div>
          <label className="label-overline block mb-1">{t("customer_phone")}</label>
          <input
            data-testid="form-order-phone"
            required
            type="tel"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            className={input}
            placeholder="+1 ..."
          />
        </div>
      </div>

      {/* Items / Products list */}
      <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-overline">{t("products_list")}</div>
          <button
            type="button"
            data-testid="form-order-add-item"
            onClick={() => setShowAddPicker(true)}
            className="font-display text-xs font-bold border border-black px-3 py-1 hover:bg-black hover:text-white"
          >
            🛒 {t("add_product_button")}
          </button>
        </div>
        <div className="space-y-3">
          {items.map((it, idx) => {
            // Phase-1 Add Product architecture: items created from the
            // Catalog / Service / Supplier flows are self-contained and
            // read-only in the item editor. Only qty, price and notes can
            // be edited inline.
            if (
              it.source_type === "catalog" ||
              it.source_type === "service" ||
              it.source_type === "supplier"
            ) {
              const isCatalog = it.source_type === "catalog";
              const isSupplier = it.source_type === "supplier";
              const badgeLabel = isSupplier
                ? "SUPPLIER"
                : isCatalog
                ? t("order_item_from_catalog_badge")
                : t("order_item_service_badge");
              const fallbackIcon = isSupplier ? "🏭" : isCatalog ? "📚" : "⚙";
              return (
                <div
                  key={idx}
                  data-testid={`form-order-item-${idx}`}
                  data-source-type={it.source_type}
                  className="bg-white border border-[var(--border)] p-3"
                >
                  <div className="flex items-start gap-3">
                    {(isCatalog || isSupplier) && it.catalog_image_url ? (
                      <img
                        src={it.catalog_image_url}
                        alt={it.description}
                        className="w-20 h-20 object-cover border border-[var(--border)] flex-shrink-0"
                      />
                    ) : (
                      <div className="w-20 h-20 flex items-center justify-center bg-[var(--surface-alt)] border border-[var(--border)] text-2xl flex-shrink-0">
                        {fallbackIcon}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono-tech text-[0.6rem] font-bold tracking-[0.2em] px-2 py-0.5 border border-black">
                          {badgeLabel}
                        </span>
                        {(isCatalog || isSupplier) && it.catalog_sku && (
                          <span className="font-mono-tech text-xs font-bold tracking-wider text-[var(--text-secondary)]">
                            {it.catalog_sku}
                          </span>
                        )}
                      </div>
                      <div className="font-display font-bold text-base line-clamp-2">
                        {it.description || "—"}
                      </div>
                      {(isCatalog || isSupplier) && (it.catalog_color || it.catalog_size) && (
                        <div className="text-xs text-[var(--text-secondary)] mt-1">
                          {it.catalog_color && (
                            <span>{t("order_item_catalog_color")}: <b>{it.catalog_color}</b></span>
                          )}
                          {it.catalog_color && it.catalog_size && <span> · </span>}
                          {it.catalog_size && (
                            <span>{t("order_item_catalog_size")}: <b>{it.catalog_size}</b></span>
                          )}
                        </div>
                      )}
                      {!isCatalog && !isSupplier && it.service_type && (
                        <div className="text-xs text-[var(--text-secondary)] mt-1">
                          {t("order_item_service_type")}: <b>{it.service_type}</b>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid={`form-order-item-remove-${idx}`}
                      onClick={() => removeItem(idx)}
                      className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-2 py-1 text-xs hover:bg-[var(--alert-overdue)] hover:text-white"
                    >
                      {t("remove_item")}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 pt-3 border-t border-[var(--border)]">
                    <div>
                      <label className="label-overline block mb-1">{t("qty")}</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={it.quantity ?? ""}
                        onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                        className={input}
                        data-testid={`form-order-item-qty-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="label-overline block mb-1">{t("unit_price")}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={it.unit_price ?? ""}
                        onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                        className={input}
                        data-testid={`form-order-item-price-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="label-overline block mb-1">{t("notes")}</label>
                      <input
                        type="text"
                        value={it.notes || ""}
                        onChange={(e) => updateItem(idx, { notes: e.target.value })}
                        className={input}
                        data-testid={`form-order-item-notes-${idx}`}
                        placeholder="—"
                      />
                    </div>
                  </div>
                  {/* Iter42 · Fase B — financial breakdown for catalog/supplier/service
                      items (gated by costs.view). Mirrors the manual-item block. */}
                  {hasFinancialAccess && (() => {
                    const live = liveItems[idx] || {};
                    const hasExtras =
                      Array.isArray(live.extra_costs) && live.extra_costs.length > 0;
                    return (
                      <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-2">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          <div>
                            <div className="label-overline mb-0.5">
                              {t("item_unit_cost")}
                            </div>
                            <input
                              data-testid={DASHBOARD.orderItemUnitCost(idx)}
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.unit_cost}
                              onChange={(e) =>
                                updateItem(idx, { unit_cost: e.target.value })
                              }
                              className={`${input} font-mono-tech tabular-nums`}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <div className="label-overline mb-0.5">
                              {t("item_subtotal")}
                            </div>
                            <div
                              data-testid={DASHBOARD.orderItemSubtotal(idx)}
                              className="w-full border border-[var(--border)] bg-[var(--surface-alt)] px-2 py-2 font-mono-tech font-bold tabular-nums text-right text-sm"
                            >
                              {money(live.subtotal)}
                            </div>
                          </div>
                          <div>
                            <div className="label-overline mb-0.5">
                              {t("item_cost_total")}
                            </div>
                            <div
                              data-testid={DASHBOARD.orderItemCostTotal(idx)}
                              className="w-full border border-[var(--border)] bg-[var(--surface-alt)] px-2 py-2 font-mono-tech tabular-nums text-right text-sm"
                            >
                              {money(live.cost_total)}
                            </div>
                          </div>
                          <div>
                            <div className="label-overline mb-0.5">
                              {t("item_profit")}
                            </div>
                            <div
                              data-testid={DASHBOARD.orderItemProfit(idx)}
                              className="w-full border px-2 py-2 font-mono-tech font-bold tabular-nums text-right text-sm"
                              style={{
                                borderColor:
                                  (live.profit || 0) >= 0
                                    ? "var(--success)"
                                    : "var(--alert-overdue)",
                                color:
                                  (live.profit || 0) >= 0
                                    ? "var(--success)"
                                    : "var(--alert-overdue)",
                                background: "white",
                              }}
                            >
                              {money(live.profit)}
                            </div>
                          </div>
                          <div>
                            <div className="label-overline mb-0.5">Extras total</div>
                            <div
                              data-testid={`form-order-item-extras-total-${idx}`}
                              className="w-full border border-[var(--border)] bg-[var(--surface-alt)] px-2 py-2 font-mono-tech tabular-nums text-right text-sm"
                            >
                              {money(live.extra_costs_total)}
                            </div>
                          </div>
                        </div>
                        {hasExtras && (
                          <div
                            data-testid={`form-order-item-extras-${idx}`}
                            className="border border-dashed border-[var(--border)] bg-[var(--surface-alt)] px-3 py-2 text-[0.72rem] font-mono-tech tabular-nums flex flex-wrap items-center gap-x-4 gap-y-1"
                          >
                            <span className="label-overline">Extras:</span>
                            {live.extra_costs.map((ec, i) => (
                              <span key={i} className="text-[var(--text-secondary)]">
                                {ec.concept || "—"}{" "}
                                <span className="text-black font-bold">
                                  ${(Number(ec.amount) || 0).toFixed(2)}
                                </span>
                                <span className="opacity-60">
                                  {" "}
                                  · {ec.type === "flat" ? "Fijo" : "×qty"}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            }

            const linked = it.product_id ? productById[it.product_id] : null;
            const ptype = it.product_type_id ? typeById[it.product_type_id] : null;
            const live = liveItems[idx] || {};
            // Sprint 5.5 — Active products + currently selected (so historical
            // orders with inactive/archived products still display the name).
            // Archived products are NEVER selectable in new orders.
            const visibleProducts = catalogProducts.filter((p) => {
              const status = p.status || (p.is_active ? "active" : "inactive");
              if (p.id === it.product_id) return true;
              return status === "active";
            });
            // Sprint SP-V3 — Smart Product linkage state for THIS item.
            const sp = it.smart_product_id ? smartProductById[it.smart_product_id] : null;
            const required = sp?.required_fields || {};
            // A field key is "asked" iff toggle is ON AND the SP has no
            // pre-configured value for it (heritage takes precedence).
            const shouldAsk = (key, heritageVal) => {
              if (!sp) return true; // no SP picked → render legacy block
              if (!required[key]) return false;
              return !heritageVal;
            };
            const heritageBrand = sp ? (sp.default_brand_id || it.brand_id) : null;
            const heritageModel = sp ? (sp.default_model || it.model) : null;
            const heritageSupplier = sp ? (sp.default_supplier_id || it.supplier_id) : null;
            const heritageUom = sp ? (sp.uom_id || it.uom_id) : null;
            const heritageProdTime =
              sp && sp.production_time_value != null
                ? `${sp.production_time_value} ${t("smart_products_production_time_unit_" + (sp.production_time_unit || "hours"))}`
                : null;
            const findName = (list, id) => (list.find((x) => x.id === id) || {}).name || "—";
            return (
              <div
                key={idx}
                data-testid={`form-order-item-${idx}`}
                className="bg-white border border-[var(--border)] p-3 space-y-3"
              >
                {/* Smart Product picker (Sprint V3 → Orders) */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 md:col-span-10">
                    <label className="label-overline block mb-1">
                      {t("order_item_smart_product")}
                    </label>
                    <select
                      data-testid={DASHBOARD.orderItemSmartProductPicker(idx)}
                      value={it.smart_product_id || ""}
                      onChange={(e) =>
                        linkItemToSmartProduct(idx, e.target.value || null)
                      }
                      className={input}
                    >
                      <option value="">
                        {t("order_item_smart_product_placeholder")}
                      </option>
                      {smartProducts.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code ? `[${p.code}] ` : ""}{p.name}
                        </option>
                      ))}
                    </select>
                    {!sp && (
                      <div className="mt-1 text-[0.65rem] font-mono-tech text-[var(--text-secondary)]">
                        ⓘ {t("order_item_no_smart_product_hint")}
                      </div>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-2 flex items-end">
                    <button
                      type="button"
                      data-testid={`form-order-item-remove-${idx}`}
                      onClick={() => removeItem(idx)}
                      className="w-full border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-2 py-2 text-xs hover:bg-[var(--alert-overdue)] hover:text-white"
                    >
                      {t("remove_item")}
                    </button>
                  </div>
                </div>

                {/* Heritage block — read-only summary of values inherited from the SP */}
                {sp && (
                  <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-[0.7rem] font-mono-tech">
                    {sp.image_url ? (
                      <div className="row-span-2 col-span-1 flex items-center justify-center">
                        <img
                          src={sp.image_url}
                          alt={sp.name}
                          data-testid={`form-order-item-${idx}-sp-image`}
                          className="w-16 h-16 object-cover border border-emerald-400"
                        />
                      </div>
                    ) : null}
                    <HeritageCell
                      label={t("smart_products_default_brand")}
                      value={heritageBrand ? findName(brandsCatalog, heritageBrand) : "—"}
                      testId={DASHBOARD.orderItemHeritageBadge(idx, "brand")}
                    />
                    <HeritageCell
                      label={t("smart_products_default_model")}
                      value={heritageModel || "—"}
                      testId={DASHBOARD.orderItemHeritageBadge(idx, "model")}
                    />
                    <HeritageCell
                      label={t("smart_products_default_supplier")}
                      value={heritageSupplier ? findName(suppliersCatalog, heritageSupplier) : "—"}
                      testId={DASHBOARD.orderItemHeritageBadge(idx, "supplier")}
                    />
                    <HeritageCell
                      label={t("smart_products_unit_of_measure")}
                      value={heritageUom ? findName(uomsCatalog, heritageUom) : "—"}
                      testId={DASHBOARD.orderItemHeritageBadge(idx, "uom")}
                    />
                    <HeritageCell
                      label={t("smart_products_production_time")}
                      value={heritageProdTime || "—"}
                      testId={DASHBOARD.orderItemHeritageBadge(idx, "prod_time")}
                    />
                  </div>
                )}

                {/* Legacy financial catalog picker — kept as advanced option,
                    only visible when no Smart Product is linked. */}
                {!sp && (
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-12">
                      <label className="label-overline block mb-1">
                        {t("catalog_product_optional")}
                      </label>
                      <select
                        data-testid={DASHBOARD.orderItemProductSelect(idx)}
                        value={it.product_id || ""}
                        onChange={(e) => linkItemToProduct(idx, e.target.value || null)}
                        className={input}
                      >
                        <option value="">{t("select_catalog_product")}</option>
                        {visibleProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.sku ? `[${p.sku}] ` : ""}{p.name}
                            {p.suggested_price > 0 ? ` — ${money(p.suggested_price)}` : ""}
                          </option>
                        ))}
                      </select>
                      {!linked && (it.description || it.product_code) ? (
                        <div className="mt-1 text-[0.65rem] font-mono-tech text-[var(--text-secondary)]">
                          ✎ {t("manual_item")}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Manual fields (code, description) — editable when no catalog link, read-only display when linked.
                    When a Smart Product is linked these become the snapshot fields and stay editable so the user
                    can fine-tune per-order (overrides the SP defaults for THIS order only). */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 sm:col-span-4">
                    <label className="label-overline block mb-1">{t("product_code")}</label>
                    <input
                      data-testid={`form-order-item-code-${idx}`}
                      value={it.product_code}
                      onChange={(e) => updateItem(idx, { product_code: e.target.value })}
                      className={input}
                      placeholder="SKU"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-8">
                    <label className="label-overline block mb-1">{t("description")}</label>
                    <input
                      data-testid={`form-order-item-desc-${idx}`}
                      value={it.description}
                      onChange={(e) => updateItem(idx, { description: e.target.value })}
                      className={input}
                      placeholder="—"
                    />
                  </div>
                </div>

                {/* Sprint SP-V3 — Variable fields driven by Smart Product.required_fields.
                    Only fields whose toggle is ON *and* are not already pre-filled by the SP
                    heritage block are rendered. */}
                {sp && (
                  <div className="border border-[var(--accent-cyan,#0E5FFF)] bg-[var(--bg)] p-3 space-y-3">
                    <div className="label-overline text-[var(--accent-cyan,#0E5FFF)]">
                      ▶ {t("v2_production_section")}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* Quantity is always asked. */}
                      <div>
                        <label className="label-overline block mb-1">{t("quantity")}</label>
                        <input
                          data-testid={DASHBOARD.orderItemDynamicField(idx, "quantity")}
                          type="number"
                          min="1"
                          step="1"
                          value={it.quantity}
                          onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                          className={`${input} font-mono-tech tabular-nums`}
                        />
                      </div>
                      {shouldAsk("brand", heritageBrand) && (
                        <div>
                          <label className="label-overline block mb-1">
                            {t("smart_products_default_brand")}
                          </label>
                          <select
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "brand")}
                            value={it.brand_id || ""}
                            onChange={(e) => updateItem(idx, { brand_id: e.target.value || null })}
                            className={input}
                          >
                            <option value="">{t("no_selection")}</option>
                            {brandsCatalog.map((b) => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {shouldAsk("model", heritageModel) && (
                        <div>
                          <label className="label-overline block mb-1">
                            {t("smart_products_default_model")}
                          </label>
                          <input
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "model")}
                            value={it.model || ""}
                            onChange={(e) => updateItem(idx, { model: e.target.value })}
                            className={input}
                          />
                        </div>
                      )}
                      {shouldAsk("color", null) && (
                        <div>
                          <label className="label-overline block mb-1">Color</label>
                          <select
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "color")}
                            value={it.color_id || ""}
                            onChange={(e) => updateItem(idx, { color_id: e.target.value || null })}
                            className={input}
                          >
                            <option value="">{t("no_selection")}</option>
                            {colorsCatalog.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {shouldAsk("size", null) && (
                        <div>
                          <label className="label-overline block mb-1">Size</label>
                          <select
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "size")}
                            value={it.size_id || ""}
                            onChange={(e) => updateItem(idx, { size_id: e.target.value || null })}
                            className={input}
                          >
                            <option value="">{t("no_selection")}</option>
                            {sizesCatalog.map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {shouldAsk("material", null) && (
                        <div>
                          <label className="label-overline block mb-1">Material</label>
                          <select
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "material")}
                            value={it.material_id || ""}
                            onChange={(e) => updateItem(idx, { material_id: e.target.value || null })}
                            className={input}
                          >
                            <option value="">{t("no_selection")}</option>
                            {materialsCatalog.map((m) => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {shouldAsk("decoration_method", null) && (
                        <div>
                          <label className="label-overline block mb-1">{t("field_decoration_method")}</label>
                          <select
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "decoration_method")}
                            value={it.decoration_method_id || ""}
                            onChange={(e) => updateItem(idx, { decoration_method_id: e.target.value || null })}
                            className={input}
                          >
                            <option value="">{t("no_selection")}</option>
                            {decorationMethods
                              .filter((d) => d.is_active || d.id === it.decoration_method_id)
                              .map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.name}{!d.is_active ? ` (${t("inactive")})` : ""}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                      {shouldAsk("logo_location", null) && (
                        <div>
                          <label className="label-overline block mb-1">{t("field_logo_location")}</label>
                          <select
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "logo_location")}
                            value={it.logo_location_id || ""}
                            onChange={(e) => updateItem(idx, { logo_location_id: e.target.value || null })}
                            className={input}
                          >
                            <option value="">{t("no_selection")}</option>
                            {logoLocations
                              .filter((l) => l.is_active || l.id === it.logo_location_id)
                              .map((l) => (
                                <option key={l.id} value={l.id}>
                                  {l.name}{!l.is_active ? ` (${t("inactive")})` : ""}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                      {shouldAsk("width", null) && (
                        <div>
                          <label className="label-overline block mb-1">{t("field_width")}</label>
                          <input
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "width")}
                            type="number" min="0" step="0.01"
                            value={it.width ?? ""}
                            onChange={(e) => updateItem(idx, { width: e.target.value === "" ? null : Number(e.target.value) })}
                            className={`${input} font-mono-tech tabular-nums`}
                            placeholder="ft"
                          />
                        </div>
                      )}
                      {shouldAsk("height", null) && (
                        <div>
                          <label className="label-overline block mb-1">{t("field_height")}</label>
                          <input
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "height")}
                            type="number" min="0" step="0.01"
                            value={it.height ?? ""}
                            onChange={(e) => updateItem(idx, { height: e.target.value === "" ? null : Number(e.target.value) })}
                            className={`${input} font-mono-tech tabular-nums`}
                            placeholder="ft"
                          />
                        </div>
                      )}
                      {shouldAsk("thickness", null) && (
                        <div>
                          <label className="label-overline block mb-1">{t("field_thickness")}</label>
                          <input
                            data-testid={DASHBOARD.orderItemDynamicField(idx, "thickness")}
                            type="number" min="0" step="0.01"
                            value={it.thickness ?? ""}
                            onChange={(e) => updateItem(idx, { thickness: e.target.value === "" ? null : Number(e.target.value) })}
                            className={`${input} font-mono-tech tabular-nums`}
                          />
                        </div>
                      )}
                      {shouldAsk("installation", null) && (
                        <div className="flex items-end">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              data-testid={DASHBOARD.orderItemDynamicField(idx, "installation")}
                              type="checkbox"
                              checked={!!it.installation}
                              onChange={(e) => updateItem(idx, { installation: e.target.checked })}
                            />
                            Installation
                          </label>
                        </div>
                      )}
                      {shouldAsk("led", null) && (
                        <div className="flex items-end">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              data-testid={DASHBOARD.orderItemDynamicField(idx, "led")}
                              type="checkbox"
                              checked={!!it.led}
                              onChange={(e) => updateItem(idx, { led: e.target.checked })}
                            />
                            LED
                          </label>
                        </div>
                      )}
                    </div>
                    {shouldAsk("comments", null) && (
                      <div>
                        <label className="label-overline block mb-1">{t("field_customer_notes")}</label>
                        <textarea
                          data-testid={DASHBOARD.orderItemDynamicField(idx, "comments")}
                          rows={2}
                          value={it.customer_notes || ""}
                          onChange={(e) => updateItem(idx, { customer_notes: e.target.value })}
                          className={input}
                          placeholder="—"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Dynamic custom fields from product type */}
                {ptype && (ptype.field_schema || []).length > 0 ? (
                  <DynamicFieldInputs
                    schema={ptype.field_schema}
                    values={it.custom_fields || {}}
                    onChange={(key, val) => updateItemCustomField(idx, key, val)}
                    testIdPrefix={`form-order-item-cf-${idx}`}
                  />
                ) : null}

                {/* Sprint 5.3 — Structured production fields (V2 architecture) — legacy fallback, hidden when a Smart Product is linked. */}
                {!sp && (
                <div className="border border-[var(--border)] bg-[var(--bg)] p-3 space-y-3">
                  <div className="label-overline text-[var(--accent-cyan,#0E5FFF)]">
                    ▶ {t("v2_production_section")}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="label-overline block mb-1">{t("field_decoration_method")}</label>
                      <select
                        data-testid={DASHBOARD.orderItemDecorationMethod(idx)}
                        value={it.decoration_method_id || ""}
                        onChange={(e) => updateItem(idx, { decoration_method_id: e.target.value || null })}
                        className={input}
                      >
                        <option value="">{t("no_selection")}</option>
                        {decorationMethods
                          .filter((d) => d.is_active || d.id === it.decoration_method_id)
                          .map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}{!d.is_active ? ` (${t("inactive")})` : ""}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="label-overline block mb-1">{t("field_logo_location")}</label>
                      <select
                        data-testid={DASHBOARD.orderItemLogoLocation(idx)}
                        value={it.logo_location_id || ""}
                        onChange={(e) => updateItem(idx, { logo_location_id: e.target.value || null })}
                        className={input}
                      >
                        <option value="">{t("no_selection")}</option>
                        {logoLocations
                          .filter((l) => l.is_active || l.id === it.logo_location_id)
                          .map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.name}{!l.is_active ? ` (${t("inactive")})` : ""}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="label-overline block mb-1">{t("field_colors_used")}</label>
                      <input
                        data-testid={DASHBOARD.orderItemColorsUsed(idx)}
                        value={it.colors_used || ""}
                        onChange={(e) => updateItem(idx, { colors_used: e.target.value })}
                        className={input}
                        placeholder={t("field_colors_used_hint")}
                      />
                    </div>
                    <div>
                      <label className="label-overline block mb-1">{t("field_artwork_url")}</label>
                      <input
                        data-testid={DASHBOARD.orderItemArtworkUrl(idx)}
                        value={it.artwork_url || ""}
                        onChange={(e) => updateItem(idx, { artwork_url: e.target.value })}
                        className={input}
                        placeholder="https://…"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label-overline block mb-1">{t("field_customer_notes")}</label>
                    <textarea
                      data-testid={DASHBOARD.orderItemCustomerNotes(idx)}
                      value={it.customer_notes || ""}
                      onChange={(e) => updateItem(idx, { customer_notes: e.target.value })}
                      rows={2}
                      className={input}
                      placeholder="—"
                    />
                  </div>

                  {it.requires_dimensions ? (
                    <div className="border border-[var(--accent-cyan,#0E5FFF)] bg-white p-3">
                      <div className="label-overline mb-2 text-[var(--accent-cyan,#0E5FFF)]">
                        ⌗ {t("dimensions_section")}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <label className="label-overline block mb-1">{t("field_width")}</label>
                          <input
                            data-testid={DASHBOARD.orderItemWidth(idx)}
                            type="number"
                            min="0"
                            step="0.01"
                            value={it.width ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              const w = v === "" ? null : Number(v);
                              const h = it.height === "" || it.height == null ? null : Number(it.height);
                              const currentArea = it.area_sqft === "" || it.area_sqft == null ? null : Number(it.area_sqft);
                              // Auto-derive area only if user hasn't manually overridden it
                              const nextArea = (w != null && h != null && (currentArea == null || currentArea === 0))
                                ? +(w * h).toFixed(2)
                                : currentArea;
                              updateItem(idx, { width: w, area_sqft: nextArea });
                            }}
                            className={`${input} font-mono-tech tabular-nums`}
                            placeholder="ft"
                          />
                        </div>
                        <div>
                          <label className="label-overline block mb-1">{t("field_height")}</label>
                          <input
                            data-testid={DASHBOARD.orderItemHeight(idx)}
                            type="number"
                            min="0"
                            step="0.01"
                            value={it.height ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              const h = v === "" ? null : Number(v);
                              const w = it.width === "" || it.width == null ? null : Number(it.width);
                              const currentArea = it.area_sqft === "" || it.area_sqft == null ? null : Number(it.area_sqft);
                              const nextArea = (w != null && h != null && (currentArea == null || currentArea === 0))
                                ? +(w * h).toFixed(2)
                                : currentArea;
                              updateItem(idx, { height: h, area_sqft: nextArea });
                            }}
                            className={`${input} font-mono-tech tabular-nums`}
                            placeholder="ft"
                          />
                        </div>
                        <div>
                          <label className="label-overline block mb-1">{t("field_thickness")}</label>
                          <input
                            data-testid={DASHBOARD.orderItemThickness(idx)}
                            type="number"
                            min="0"
                            step="0.01"
                            value={it.thickness ?? ""}
                            onChange={(e) => updateItem(idx, {
                              thickness: e.target.value === "" ? null : Number(e.target.value),
                            })}
                            className={`${input} font-mono-tech tabular-nums`}
                            placeholder="—"
                          />
                        </div>
                        <div>
                          <label className="label-overline block mb-1">
                            {t("field_area_sqft")}
                          </label>
                          <input
                            data-testid={DASHBOARD.orderItemAreaSqft(idx)}
                            type="number"
                            min="0"
                            step="0.01"
                            value={it.area_sqft ?? ""}
                            onChange={(e) => updateItem(idx, {
                              area_sqft: e.target.value === "" ? null : Number(e.target.value),
                            })}
                            className={`${input} font-mono-tech tabular-nums font-bold`}
                            placeholder="—"
                          />
                          <div className="text-[10px] font-mono-tech text-[var(--text-secondary)] mt-0.5">
                            {t("field_area_sqft_auto")}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                )}

                {/* Financial row: qty, unit price, unit cost + live subtotal/cost/profit */}
                <div className="grid grid-cols-12 gap-2 items-end">
                  {!sp && (
                  <div className="col-span-6 md:col-span-2">
                    <label className="label-overline block mb-1">{t("quantity")}</label>
                    <input
                      data-testid={`form-order-item-qty-${idx}`}
                      type="number"
                      min="0"
                      step="1"
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                      className={`${input} font-mono-tech tabular-nums`}
                    />
                  </div>
                  )}
                  <div className="col-span-6 md:col-span-2">
                    <label className="label-overline block mb-1">{t("item_unit_price")}</label>
                    <input
                      data-testid={DASHBOARD.orderItemUnitPrice(idx)}
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.unit_price}
                      onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                      className={`${input} font-mono-tech tabular-nums`}
                      placeholder="0.00"
                    />
                  </div>
                  {hasFinancialAccess && (
                  <div className="col-span-6 md:col-span-2">
                    <label className="label-overline block mb-1">{t("item_unit_cost")}</label>
                    <input
                      data-testid={DASHBOARD.orderItemUnitCost(idx)}
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.unit_cost}
                      onChange={(e) => updateItem(idx, { unit_cost: e.target.value })}
                      className={`${input} font-mono-tech tabular-nums`}
                      placeholder="0.00"
                    />
                  </div>
                  )}
                  <div className="col-span-6 md:col-span-2">
                    <label className="label-overline block mb-1">{t("item_subtotal")}</label>
                    <div
                      data-testid={DASHBOARD.orderItemSubtotal(idx)}
                      className="w-full border border-[var(--border)] bg-[var(--surface-alt)] px-3 py-2 font-mono-tech font-bold tabular-nums text-right"
                    >
                      {money(live.subtotal)}
                    </div>
                  </div>
                  {hasFinancialAccess && (
                  <div className="col-span-6 md:col-span-2">
                    <label className="label-overline block mb-1">{t("item_cost_total")}</label>
                    <div
                      data-testid={DASHBOARD.orderItemCostTotal(idx)}
                      className="w-full border border-[var(--border)] bg-[var(--surface-alt)] px-3 py-2 font-mono-tech tabular-nums text-right"
                    >
                      {money(live.cost_total)}
                    </div>
                  </div>
                  )}
                  {hasFinancialAccess && (
                  <div className="col-span-6 md:col-span-2">
                    <label className="label-overline block mb-1">{t("item_profit")}</label>
                    <div
                      data-testid={DASHBOARD.orderItemProfit(idx)}
                      className="w-full border px-3 py-2 font-mono-tech font-bold tabular-nums text-right"
                      style={{
                        borderColor: live.profit >= 0 ? "var(--success)" : "var(--alert-overdue)",
                        color: live.profit >= 0 ? "var(--success)" : "var(--alert-overdue)",
                        background: "white",
                      }}
                    >
                      {money(live.profit)}
                    </div>
                  </div>
                  )}
                </div>

                {/* Iter42 · Fase B — per-item extras breakdown line
                    (visible only if user has costs.view AND item has extras).
                    Read-only summary; extras are edited in the catalog step. */}
                {hasFinancialAccess &&
                  Array.isArray(live.extra_costs) &&
                  live.extra_costs.length > 0 && (
                    <div
                      data-testid={`form-order-item-extras-${idx}`}
                      className="border border-dashed border-[var(--border)] bg-[var(--surface-alt)] px-3 py-2 text-[0.72rem] font-mono-tech tabular-nums flex flex-wrap items-center gap-x-4 gap-y-1"
                    >
                      <span className="label-overline">Extras:</span>
                      {live.extra_costs.map((ec, i) => (
                        <span key={i} className="text-[var(--text-secondary)]">
                          {(ec.concept || "—")}{" "}
                          <span className="text-black font-bold">
                            ${(Number(ec.amount) || 0).toFixed(2)}
                          </span>
                          <span className="opacity-60">
                            {" "}
                            · {ec.type === "flat" ? "Fijo" : "×qty"}
                          </span>
                        </span>
                      ))}
                      <span className="ml-auto">
                        Extras total{" "}
                        <span className="font-bold text-black">
                          {money(live.extra_costs_total)}
                        </span>
                      </span>
                    </div>
                  )}

                {/* Item notes */}
                <div>
                  <label className="label-overline block mb-1">{t("item_notes")}</label>
                  <input
                    data-testid={`form-order-item-notes-${idx}`}
                    value={it.notes}
                    onChange={(e) => updateItem(idx, { notes: e.target.value })}
                    className={input}
                    placeholder="—"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("delivery_date")}</label>
          <input
            data-testid={FORM.orderDelivery}
            required
            type="date"
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
            className={input}
          />
        </div>
        <div>
          <label className="label-overline block mb-1">{t("status")}</label>
          <select
            data-testid={FORM.orderStatus}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={input}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`order_status_${s}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-4">
        <div className="label-overline mb-3" style={{ color: "var(--accent-cyan)" }}>
          $ — {t("kpi_section_financial")}
        </div>

        {/* Sprint 4 — Live financial rollup (read-only).
            Iter42 · Fase B — Order-level cost / profit / margin are hidden
            for users without `costs.view` (backend also nulls them). The
            subtotal (revenue) stays visible so employees can still see the
            order total they must charge the customer. */}
        <div
          className={`grid ${
            hasFinancialAccess ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1"
          } gap-3 mb-4`}
        >
          <div>
            <div className="label-overline mb-1">{t("order_subtotal")}</div>
            <div
              data-testid={DASHBOARD.orderTotalsSubtotal}
              className="w-full border border-[var(--border)] bg-white px-3 py-2 font-mono-tech font-bold tabular-nums text-right text-base"
            >
              {money(orderSubtotal)}
            </div>
          </div>
          {hasFinancialAccess && (
          <div>
            <div className="label-overline mb-1">{t("order_total_cost")}</div>
            <div
              data-testid={DASHBOARD.orderTotalsCost}
              className="w-full border border-[var(--border)] bg-white px-3 py-2 font-mono-tech tabular-nums text-right text-base"
            >
              {money(orderCost)}
            </div>
          </div>
          )}
          {hasFinancialAccess && (
          <div>
            <div className="label-overline mb-1">{t("order_total_profit")}</div>
            <div
              data-testid={DASHBOARD.orderTotalsProfit}
              className="w-full border px-3 py-2 font-mono-tech font-bold tabular-nums text-right text-base"
              style={{
                borderColor: orderProfit >= 0 ? "var(--success)" : "var(--alert-overdue)",
                color: orderProfit >= 0 ? "var(--success)" : "var(--alert-overdue)",
                background: "white",
              }}
            >
              {money(orderProfit)}
            </div>
          </div>
          )}
          {hasFinancialAccess && (
          <div>
            <div className="label-overline mb-1">{t("order_margin")}</div>
            <div
              data-testid={DASHBOARD.orderTotalsMargin}
              className="w-full border border-[var(--border)] bg-white px-3 py-2 font-mono-tech font-bold tabular-nums text-right text-base"
            >
              {orderMargin.toFixed(2)}%
            </div>
          </div>
          )}
        </div>

        {/* Iter46 · Fase C — Resumen Comercial. Visible to ALL users
            (vendors need discount/tax/balance to close a sale). Cost /
            profit / margin remain hidden behind hasFinancialAccess above. */}
        {anyPriced && (
          <div
            data-testid="form-order-commercial-summary"
            className="border border-[var(--border)] bg-[var(--surface-alt)] p-3 mb-4"
          >
            <div className="label-overline mb-2">Resumen Comercial</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="label-overline block mb-1">Subtotal</label>
                <div
                  data-testid="form-order-commercial-subtotal"
                  className="w-full border border-[var(--border)] bg-white px-3 py-2 font-mono-tech tabular-nums text-right"
                >
                  ${orderSubtotal.toFixed(2)}
                </div>
              </div>
              <div>
                <label className="label-overline block mb-1">
                  Descuento
                </label>
                <div className="flex items-stretch gap-1">
                  <select
                    data-testid="form-order-discount-type"
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    className="px-2 py-2 border border-[var(--border)] bg-white text-sm focus:border-[var(--primary)]"
                  >
                    <option value="amount">$</option>
                    <option value="percent">%</option>
                  </select>
                  <input
                    data-testid="form-order-discount-value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className={`${input} font-mono-tech tabular-nums text-right`}
                    placeholder="0.00"
                  />
                </div>
                {commercialRollup.discount_amount > 0 && (
                  <div className="text-[10px] font-mono-tech text-[var(--text-secondary)] mt-0.5 tabular-nums">
                    − ${commercialRollup.discount_amount.toFixed(2)}
                  </div>
                )}
              </div>
              <div>
                <label className="label-overline block mb-1 flex items-center justify-between">
                  <span>Sales Tax (9.75%)</span>
                  <label className="flex items-center gap-1 text-[10px] font-mono-tech font-bold normal-case cursor-pointer">
                    <input
                      data-testid="form-order-tax-exempt"
                      type="checkbox"
                      checked={taxExempt}
                      onChange={(e) => setTaxExempt(e.target.checked)}
                      className="w-3 h-3"
                    />
                    Tax Exempt
                  </label>
                </label>
                <div
                  data-testid="form-order-sales-tax"
                  className="w-full border border-[var(--border)] bg-white px-3 py-2 font-mono-tech tabular-nums text-right"
                  style={taxExempt ? { color: "var(--text-secondary)" } : {}}
                >
                  ${commercialRollup.sales_tax_amount.toFixed(2)}
                </div>
              </div>
              <div>
                <label className="label-overline block mb-1">Total</label>
                <div
                  data-testid="form-order-commercial-total"
                  className="w-full border border-black bg-white px-3 py-2 font-mono-tech font-bold tabular-nums text-right text-base"
                >
                  ${commercialRollup.total_amount.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label-overline block mb-1">{t("total_amount")}</label>
            <input
              data-testid="form-order-total"
              required
              type="number"
              min="0"
              step="0.01"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              className={`${input} ${anyPriced ? "bg-[var(--surface-alt)] text-[var(--text-secondary)]" : ""}`}
              placeholder="0.00"
              readOnly={anyPriced}
              title={anyPriced ? t("order_subtotal") : ""}
            />
          </div>
          <div>
            <label className="label-overline block mb-1">{t("deposit_received")}</label>
            <input
              data-testid="form-order-deposit"
              required
              type="number"
              min="0"
              step="0.01"
              value={depositReceived}
              onChange={(e) => setDepositReceived(e.target.value)}
              className={input}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="label-overline block mb-1">{t("pending_balance")}</label>
            <div
              data-testid="form-order-balance"
              className="w-full border border-[var(--border)] px-3 py-2 font-mono-tech font-bold tabular-nums"
              style={{
                background: "white",
                color: balance > 0 ? "var(--primary)" : "var(--success)",
              }}
            >
              ${balance.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="label-overline block mb-1">{t("notes")}</label>
        <textarea
          data-testid={FORM.orderNotes}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className={input}
        />
      </div>

      {/* Import Invoice PDF (deferred: order must already exist) */}
      <div className="border-2 border-dashed border-black/30 bg-white p-5">
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="font-display font-black text-base tracking-tight">
              {t("import_invoice_pdf")}
            </div>
            {!orderId && (
              <div className="text-xs text-[var(--text-secondary)] mt-1">
                {t("invoice_pdf_save_first")}
              </div>
            )}
          </div>
          {invoicePdfFilename && (
            <span className="font-mono-tech text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
              ✓ {t("invoice_pdf_attached")}
            </span>
          )}
        </div>

        <input
          ref={fileInputRef}
          data-testid={DASHBOARD.invoicePdfInput}
          type="file"
          accept="application/pdf,.pdf"
          onChange={handleInvoicePdfChange}
          className="hidden"
        />

        {orderId && !invoicePdfFilename && (
          <button
            type="button"
            data-testid={DASHBOARD.invoicePdfImportBtn}
            onClick={handleInvoicePdfClick}
            disabled={pdfUploading}
            className="w-full border-2 border-black bg-black text-white px-6 py-4 text-base font-display font-black tracking-wide hover:bg-white hover:text-black transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {pdfUploading ? t("invoice_pdf_uploading") : t("invoice_pdf_select")}
          </button>
        )}

        {orderId && invoicePdfFilename && (
          <div className="space-y-3">
            <div className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 flex items-center gap-3">
              <div className="bg-black text-white px-2 py-1 font-mono-tech text-[10px] font-bold tracking-wider">PDF</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate text-sm">
                  {invoicePdfFilename}
                </div>
                {invoicePdfUploadedAt && (
                  <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] mt-0.5">
                    {t("invoice_pdf_uploaded_at")}: {formatDateTime(invoicePdfUploadedAt, lang)}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid={DASHBOARD.invoicePdfViewBtn}
                onClick={handleInvoicePdfView}
                className="flex-1 min-w-[120px] border-2 border-black bg-white px-4 py-3 text-sm font-display font-bold tracking-wide hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {t("invoice_pdf_view")}
              </button>
              <button
                type="button"
                data-testid={DASHBOARD.invoicePdfImportBtn}
                onClick={handleInvoicePdfClick}
                disabled={pdfUploading}
                className="flex-1 min-w-[120px] border-2 border-[var(--border)] px-4 py-3 text-sm font-display font-bold tracking-wide hover:bg-[var(--surface-alt)] disabled:opacity-50"
              >
                {pdfUploading ? t("invoice_pdf_uploading") : t("invoice_pdf_replace")}
              </button>
              <button
                type="button"
                data-testid={DASHBOARD.invoicePdfRemoveBtn}
                onClick={handleInvoicePdfRemove}
                disabled={pdfUploading}
                className="min-w-[120px] border-2 border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-4 py-3 text-sm font-display font-bold tracking-wide hover:bg-[var(--alert-overdue)] hover:text-white disabled:opacity-50"
              >
                {t("invoice_pdf_remove")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button
          data-testid={FORM.orderCancel}
          type="button"
          onClick={onCancel}
          className="flex-1 border border-[var(--border)] px-4 py-2.5 font-display font-medium hover:bg-[var(--surface-alt)]"
        >
          {t("cancel")}
        </button>
        <button
          data-testid={FORM.orderSubmit}
          type="submit"
          disabled={loading}
          className="flex-1 bg-[var(--text-primary)] text-white px-4 py-2.5 font-display font-bold hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {loading ? "..." : t("save")}
        </button>
      </div>

      {/* Feb-2026 · Add Product architecture v3:
          One universal catalog — no more supplier picker layer. */}
      <AddProductRouter
        open={showAddPicker}
        onClose={() => setShowAddPicker(false)}
        onPick={handlePickAddOption}
      />
      <CatalogPicker
        open={showCatalog}
        onClose={() => setShowCatalog(false)}
        onAdd={appendCatalogOrServiceItems}
      />
      <ServiceItemForm
        open={showServiceForm}
        onClose={() => setShowServiceForm(false)}
        onAdd={appendCatalogOrServiceItems}
      />
    </form>
  );
}

// HeritageCell — read-only display of a value inherited from a Smart Product.
function HeritageCell({ label, value, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col">
      <span className="text-[0.55rem] uppercase tracking-widest text-emerald-700">
        {label}
      </span>
      <span className="font-mono-tech text-emerald-900 truncate">{value}</span>
    </div>
  );
}
